// Runtime favicon badging — the GitHub trick: draw the existing favicon onto a
// canvas, overlay a small colored status dot, and swap the tab icon to the
// generated image. Used by useFaviconBadge to show manual-analysis progress
// (amber) and the batch result (green / red) without shipping extra icon files.

export type BadgeColor = 'amber' | 'green' | 'red';

// GitHub-ish status colors; ring uses the page theme bg for contrast on any icon.
const COLORS: Record<BadgeColor, string> = {
	amber: '#dbab09',
	green: '#2da44e',
	red: '#cf222e',
};
const RING_COLOR = '#050609'; // matches <meta name="theme-color"> in index.html

const CANVAS_SIZE = 64;

let iconLink: HTMLLinkElement | null = null;
let originalHref: string | null = null;
let baseImagePromise: Promise<HTMLImageElement> | null = null;

// Bumped on every set/clear; an in-flight async draw bails if it's been
// superseded, so rapid state changes never paint a stale badge.
let generation = 0;

/** Find (and cache) the <link rel="icon"> plus its resting href. */
function getIconLink(): HTMLLinkElement | null {
	if (iconLink && iconLink.isConnected) return iconLink;
	const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
	if (!link) return null;
	iconLink = link;
	if (originalHref === null) originalHref = link.href;
	return link;
}

/** Lazily load the resting favicon as an Image so the canvas isn't tainted. */
function loadBaseImage(href: string): Promise<HTMLImageElement> {
	if (!baseImagePromise) {
		baseImagePromise = new Promise((resolve, reject) => {
			const img = new Image();
			// Same-origin asset; no crossOrigin needed. Decoding errors reject so the
			// caller can give up gracefully rather than hang.
			img.onload = () => resolve(img);
			img.onerror = () => reject(new Error('favicon base image failed to load'));
			img.src = href;
		});
	}
	return baseImagePromise;
}

/** Draw the base favicon plus a colored dot, then point the tab icon at it. */
export function setFaviconBadge(color: BadgeColor): void {
	const link = getIconLink();
	if (!link || originalHref === null) return;

	const myGen = ++generation;

	loadBaseImage(originalHref)
		.then((img) => {
			if (myGen !== generation) return; // superseded by a newer state change
			const canvas = document.createElement('canvas');
			canvas.width = CANVAS_SIZE;
			canvas.height = CANVAS_SIZE;
			const ctx = canvas.getContext('2d');
			if (!ctx) return;

			ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);

			// Bottom-right dot with a contrasting ring so it reads on any icon.
			const cx = CANVAS_SIZE * 0.8;
			const cy = CANVAS_SIZE * 0.8;
			const dotR = CANVAS_SIZE * 0.16;

			ctx.beginPath();
			ctx.arc(cx, cy, dotR + CANVAS_SIZE * 0.06, 0, Math.PI * 2);
			ctx.fillStyle = RING_COLOR;
			ctx.fill();

			ctx.beginPath();
			ctx.arc(cx, cy, dotR, 0, Math.PI * 2);
			ctx.fillStyle = COLORS[color];
			ctx.fill();

			link.href = canvas.toDataURL('image/png');
		})
		.catch(() => {
			// Keep the resting favicon on failure — badging is best-effort.
		});
}

/** Restore the resting favicon. */
export function clearFaviconBadge(): void {
	generation++; // cancel any in-flight draw
	const link = getIconLink();
	if (link && originalHref !== null) link.href = originalHref;
}
